import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  buscarDetalheAuditoria,
  buscarEmpresasAuditaveis,
  buscarTrilha,
} from "./api";
import type {
  AuditoriaDetalhe,
  EmpresaAuditavel,
  FiltrosTrilha,
  ItemAuditoriaResumo,
} from "./tipos";

/*
 * Hooks de server-state da trilha de auditoria (FE-5 CP-B — ADR 0026 §Estado /
 * TanStack Query). Toda leitura passa pelo Query: cache, dedup e revalidacao com
 * a config conservadora do `criarQueryClient` (staleTime 30s, retry 1 em query).
 *
 * Convencoes de queryKey: prefixo `"auditoria"` + recurso + parametros que afetam
 * o resultado (empresaId, filtros, registroId). A trilha inclui o OBJETO de
 * `filtros` na key: trocar pagina (offset) ou qualquer filtro e uma query
 * distinta no cache — refetch automatico ao mudar.
 *
 * `enabled` (gate de disparo): impede o fetch enquanto o(s) id(s) essencial(is)
 * nao existe(m). A auditoria e SO LEITURA — sem mutations.
 */

/** Empresas auditaveis pelo escopo (`ver_auditoria`) do usuario. */
export function useEmpresasAuditaveis(): UseQueryResult<EmpresaAuditavel[]> {
  return useQuery({
    queryKey: ["auditoria", "empresas"],
    queryFn: buscarEmpresasAuditaveis,
  });
}

/**
 * Trilha resumida de uma empresa, parametrizada por `filtros`. So dispara quando
 * ha empresa selecionada (`empresaId !== null`) — antes disso a query fica ociosa
 * (`enabled: false`), sem fetch nem erro. A queryKey carrega `empresaId` + o
 * objeto `filtros`: trocar qualquer filtro/pagina e uma query distinta no cache,
 * disparando refetch.
 */
export function useTrilhaAuditoria(
  empresaId: string | null,
  filtros: FiltrosTrilha,
): UseQueryResult<ItemAuditoriaResumo[]> {
  return useQuery({
    queryKey: ["auditoria", "trilha", empresaId, filtros],
    // `empresaId as string` e seguro: `enabled` garante que a queryFn so roda
    // com empresa nao-nula. O TanStack so invoca a queryFn quando `enabled` e true.
    queryFn: () => buscarTrilha(empresaId as string, filtros),
    enabled: empresaId !== null,
  });
}

/**
 * Detalhe de um registro da trilha (com `antes`/`depois` mascarados). So dispara
 * quando ha empresa E registro selecionados — antes disso fica ociosa. A queryKey
 * inclui ambos os ids.
 */
export function useDetalheAuditoria(
  empresaId: string | null,
  registroId: string | null,
): UseQueryResult<AuditoriaDetalhe> {
  return useQuery({
    queryKey: ["auditoria", "detalhe", empresaId, registroId],
    // `as string` seguro: `enabled` garante queryFn so com ambos nao-nulos.
    queryFn: () =>
      buscarDetalheAuditoria(empresaId as string, registroId as string),
    enabled: empresaId !== null && registroId !== null,
  });
}
